import { call, put, select, takeEvery, all } from 'redux-saga/effects'

import api from '../../utils/api'
import * as apiErrorHelpers from '../../helpers/apiErrorHelpers'
import { renderSuccessNotification, renderErrorNotification } from '../../helpers/apiHelpers'
import * as sessionHelpers from '../../helpers/sessionHelpers'
import { mungeShopOrderParams } from '../../helpers/paramsHelpers'
import { loadCustomers } from './customerSaga'
import { loadAffiliations } from './affiliationSaga'
import { loadOrderIssues } from './orderIssueSaga'
import SplitClient from "../../utils/splitClient";

export function* loadShopOrders(action) {
  try {
    yield put({ type: 'SET_LOADING' })
    yield put({ type: 'UNSET_LOADING_ERROR' })

    api.setAuthApi({
      'Authorization': sessionHelpers.getApplicationCookie('authentication_token')
    })

    // load affiliations if not already loaded
    const currentAffiliationsState = (state) => state.affiliations.affiliations
    let currentAffiliations = yield select(currentAffiliationsState)
    if (currentAffiliations.length === 0) yield call(
      loadAffiliations,
      { payload: { skipLoading: true } }
    )

    const { SHOP_ORDERS } = yield call(SplitClient.getTreatment.bind(SplitClient),"SHOP_ORDERS", { email: sessionHelpers.getApplicationCookie('current_user_email') })
    const currentFiltersState = (state) => state.shopOrderFilters.currentFilters
    let currentFilters = yield select(currentFiltersState)

    if (SHOP_ORDERS === 'V1') {
      const request = {
        url: '/shop_orders',
        body: mungeShopOrderParams(currentFilters)
      }

      const response = yield call(api.utility.get, api.path(request.url), {body: request.body})

      apiErrorHelpers.catchErrors(response.body)

      yield put({
        type: 'SHOP_ORDERS_LOAD_SUCCEEDED',
        payload: {data: response.body}
      })

      // load customers of the fetched orders
      const customerIds = response.body.orders.map(so => so.originator_id)
      yield call(loadCustomers, {payload: {skipLoading: true, customerIds: customerIds}})
    } else {
      const shopOrdersRequest = {
        body: mungeShopOrderParams(currentFilters)
      }

      const [shopOrders, orderCategoryCount] = yield all([
        call(api.utility.get, api.path('/shop_orders/order_data'), { body: shopOrdersRequest.body }),
        call(api.utility.get, api.path('/shop_orders/order_category_count'),{body: shopOrdersRequest.body})
      ])

      apiErrorHelpers.catchErrors(shopOrders.body)
      apiErrorHelpers.catchErrors(orderCategoryCount.body)
      const result ={...shopOrders.body,...orderCategoryCount.body}
      yield put({
        type: 'SHOP_ORDERS_LOAD_SUCCEEDED',
        payload: { data: result}
      })

      // load customers of the fetched orders
      const customerIds = result.orders.map(so => so.originator_id)
      yield call(loadCustomers, { payload: { skipLoading: true, customerIds: customerIds } })
    }

    // load order issues
    yield call(loadOrderIssues, { payload: { skipLoading: true } })

    yield put({ type: 'UNSET_LOADING'})
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'LOADING_FAILED' })
    yield put({ type: 'SET_LOADING_ERROR' })
    yield put({ type: 'UNSET_LOADING' })

    yield put(renderErrorNotification(`Error fetching data: ${apiErrorHelpers.toString(errors)}`))

    apiErrorHelpers.consoleLog(errors)
  }
} // loadShopOrders()

export function* loadShopOrder(action) {
  try {
    yield put({ type: 'SET_TOOLTIP_LOADING' })

    api.setAuthApi({ 'Authorization': sessionHelpers.getApplicationCookie('authentication_token') })

    const id = action.payload.id
    const request = { url: `/shop_orders/${id}` }
    const response = yield call(api.utility.get, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: 'SHOP_ORDER_LOAD_SUCCEEDED',
      payload: { data: response.body }
    })

    // load affiliations if not already loaded
    const currentAffiliationsState = (state) => state.affiliations.affiliations
    let currentAffiliations = yield select(currentAffiliationsState)
    if (currentAffiliations.length === 0) yield call(loadAffiliations, { payload: { skipLoading: true }})

    // load customer if not already loaded
    const customerId = response.body.order.originator_id
    const currentCustomersState = (state) => state.customers.customers
    let currentCustomers = yield select(currentCustomersState)
    if (currentCustomers.find(c => c.id === customerId) == null) {
      yield call(loadCustomers, { payload: { skipLoading: true, customerIds: [customerId] } })
    }

    yield put({ type: 'UNSET_TOOLTIP_LOADING'})
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'UNSET_TOOLTIP_LOADING' })

    yield put(renderErrorNotification(`Error fetching data: ${apiErrorHelpers.toString(errors)}`))

    apiErrorHelpers.consoleLog(errors)
  }
} // loadShopOrder()

export function* loadShopOrderTotals(action) {
  try {
    yield put({ type: 'SET_SHOP_ORDER_TOTALS_LOADING' })
    yield put({ type: 'UNSET_LOADING_ERROR' })

    api.setAuthApi({ 'Authorization': sessionHelpers.getApplicationCookie('authentication_token') })

    const currentFiltersState = (state) => state.shopOrderFilters.currentFilters
    let currentFilters = yield select(currentFiltersState)
    const request = {
      url: '/shop_orders/totals',
      body: mungeShopOrderParams(currentFilters)
    }

    const response = yield call(api.utility.get, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: 'SHOP_ORDERS_TOTALS_LOAD_SUCCEEDED',
      payload: {
        data: response.body
      }
    })

    yield put({ type: 'UNSET_SHOP_ORDER_TOTALS_LOADING'})
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'LOADING_FAILED' })
    yield put({ type: 'SET_LOADING_ERROR' })
    yield put({ type: 'UNSET_SHOP_ORDER_TOTALS_LOADING' })

    yield put(renderErrorNotification(`Error fetching data: ${apiErrorHelpers.toString(errors)}`))

    apiErrorHelpers.consoleLog(errors)
  }
} // loadShopOrderTotals()

export function* filterShopOrders(action) {
  try {
    yield put({ type: 'SET_LOADING' })
    yield put({ type: 'UNSET_LOADING_ERROR' })

    const payload = action.payload || {}

    api.setAuthApi({
      'Authorization': sessionHelpers.getApplicationCookie('authentication_token')
    })

    yield put({ type: 'SHOP_ORDERS_FILTER_REQUESTED' })

    const currentFiltersState = (state) => state.shopOrderFilters.currentFilters
    let currentFilters = yield select(currentFiltersState)

    const {SHOP_ORDERS} = yield call(SplitClient.getTreatment.bind(SplitClient),"SHOP_ORDERS", { email: sessionHelpers.getApplicationCookie('current_user_email') })

    if (SHOP_ORDERS === 'V1') {
      const request = {
        url: '/shop_orders',
        body: mungeShopOrderParams({...currentFilters, ...payload.data})
      }

      const response = yield call(api.utility.get, api.path(request.url), {body: request.body})

      apiErrorHelpers.catchErrors(response.body)

      // clear all notifications on success
      yield put({type: 'SHOP_ORDER_NOTIFICATIONS_DELETE_ALL'})

      yield put({
        type: 'SHOP_ORDERS_FILTER_SUCCEEDED',
        payload: {
          data: response.body
        }
      })

      const customerIds = response.body.orders.map(so => so.originator_id)
      yield call(loadCustomers, {payload: {skipLoading: true, customerIds: customerIds}})
    } else {
      const shopOrdersRequest = {
        body: mungeShopOrderParams({ ...currentFilters, ...payload.data })
      }

      const [shopOrders, orderCategoryCount] = yield all([
        call(api.utility.get, api.path('/shop_orders/order_data'), { body: shopOrdersRequest.body }),
        call(api.utility.get, api.path('/shop_orders/order_category_count'),{body: shopOrdersRequest.body})
      ])

      const result ={...shopOrders.body,...orderCategoryCount.body}

      // clear all notifications on success
      yield put({ type: 'SHOP_ORDER_NOTIFICATIONS_DELETE_ALL' })

      yield put({
        type: 'SHOP_ORDERS_FILTER_SUCCEEDED',
        payload: {
          data: result
        }
      })

      const customerIds = result.orders.map(so => so.originator_id)
      yield call(loadCustomers, { payload: { skipLoading: true, customerIds: customerIds } })
    }
    yield put({ type: 'UNSET_LOADING'})
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'LOADING_FAILED' })
    yield put({ type: 'SET_LOADING_ERROR' })
    yield put({ type: 'UNSET_LOADING' })

    yield put(renderErrorNotification(`Error fetching data: ${apiErrorHelpers.toString(errors)}`))

    apiErrorHelpers.consoleLog(errors)
  }
} // filterShopOrders()

export function* updateShopOrders(action) {
  try {
    const {SHOP_ORDERS} = yield call(SplitClient.getTreatment.bind(SplitClient),"SHOP_ORDERS", { email: sessionHelpers.getApplicationCookie('current_user_email') })

    if (SHOP_ORDERS === 'V1') {
      yield put({ type: 'SET_LOADING' })
    } else {
      yield put({ type: 'SET_TOOLTIP_LOADING' })
    }

    yield put({ type: 'UNSET_LOADING_ERROR' })

    api.setAuthApi({
      'Authorization': sessionHelpers.getApplicationCookie('authentication_token')
    })

    yield put({ type: 'SHOP_ORDERS_UPDATE_REQUESTED' })

    const id = action.payload.id
    const request = {
      url: `/shop_orders/${id}`,
      body: action.payload
    }

    const response = yield call(api.utility.put, api.path(request.url), { body: request.body })

    apiErrorHelpers.catchErrors(response.body)

    yield put({
      type: 'SHOP_ORDERS_UPDATE_SUCCEEDED',
      payload: {
        data: response.body
      }
    })
    yield put(renderSuccessNotification(`Updated Shop Order id ${id}`))

    yield put({ type: 'UNSET_LOADING'})
    yield put({ type: 'UNSET_TOOLTIP_LOADING' })
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'LOADING_FAILED' })
    yield put({ type: 'SET_LOADING_ERROR' })
    yield put({ type: 'UNSET_LOADING' })
    yield put({ type: 'UNSET_TOOLTIP_LOADING' })

    const id = action.payload.id
    yield put(renderErrorNotification(
      `Error updating Shop Order id ${id}: ${apiErrorHelpers.toString(errors)}`
    ))

    apiErrorHelpers.consoleLog(errors)
  }
} // updateShopOrders()

export function* refundShopOrders(action) {
  try {
    yield put({ type: 'SET_LOADING' })
    yield put({ type: 'UNSET_LOADING_ERROR' })
    const payload = action.payload || {}

    api.setAuthApi({ 'Authorization': sessionHelpers.getApplicationCookie('authentication_token') })

    const { id, amount, invoiceRefund, sendText } = payload
    const request = {
      url: `/shop_orders/${id}/refund`,
      body: { amount: amount, invoice_refund: invoiceRefund, send_text: sendText },
    }
    const response = yield call(api.utility.put, api.path(request.url), { body: request.body })
    apiErrorHelpers.catchErrors(response.body)

    yield put(renderSuccessNotification(`Successfully issued refund for Shop Order id ${id}`))
    // reload the order
    yield put({
      type: 'SHOP_ORDERS_UPDATE_SUCCEEDED',
      payload: {
        data: response.body
      }
    })

    yield put({ type: 'UNSET_LOADING'})
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'LOADING_FAILED' })
    yield put({ type: 'SET_LOADING_ERROR' })
    yield put({ type: 'UNSET_LOADING' })

    const id = action.payload.id
    yield put(renderErrorNotification(
      `Error issuing refund for Shop Order id ${id}: ${apiErrorHelpers.toString(errors)}`
    ))

    apiErrorHelpers.consoleLog(errors)
  }
} // refundShopOrders()


export function* resolveShopOrders(action) {
  try {
    yield put({ type: 'SET_LOADING' })
    yield put({ type: 'UNSET_LOADING_ERROR' })

    api.setAuthApi({ 'Authorization': sessionHelpers.getApplicationCookie('authentication_token') })

    const id = action.payload.id
    const request = { url: `/shop_orders/${id}/resolve` }
    const response = yield call(api.utility.put, api.path(request.url), { body: request.body })
    apiErrorHelpers.catchErrors(response.body)

    yield put(renderSuccessNotification(`Successfully resolved Shop Order id ${id}`))
    yield put({
      type: 'SHOP_ORDERS_UPDATE_SUCCEEDED',
      payload: {
        data: response.body
      }
    })
    yield put({ type: 'UNSET_LOADING'})
  }
  catch (errors) {
    // eslint-disable-next-line
    errors = apiErrorHelpers.formatInternalErrors(errors)

    yield put({ type: 'LOADING_FAILED' })
    yield put({ type: 'SET_LOADING_ERROR' })
    yield put({ type: 'UNSET_LOADING' })

    const id = action.payload.id
    yield put(renderErrorNotification(
      `Error resolving Shop Order id ${id}: ${apiErrorHelpers.toString(errors)}`
    ))

    apiErrorHelpers.consoleLog(errors)
  }
} // resolveShopOrders()


export default function* shopOrderSaga() {
  yield takeEvery('SHOP_ORDERS_LOAD_SAGA', loadShopOrders)
  yield takeEvery('SHOP_ORDER_LOAD_SAGA', loadShopOrder)
  yield takeEvery('SHOP_ORDERS_TOTALS_LOAD_SAGA', loadShopOrderTotals)
  yield takeEvery('SHOP_ORDERS_FILTER_SAGA', filterShopOrders)
  yield takeEvery('SHOP_ORDERS_UPDATE_SAGA', updateShopOrders)
  yield takeEvery('SHOP_ORDERS_REFUND_SAGA', refundShopOrders)
  yield takeEvery('SHOP_ORDERS_RESOLUTION_SAGA', resolveShopOrders)
}
